import { WarningIcon } from "@phosphor-icons/react"

import { type AggregatedAdjustment } from "~/models/adjustment"
import { formatNumber } from "~/utils/formatting"

type Props = {
  adjustment: AggregatedAdjustment
  /** How many variants are about to be added (not already on the adjustment). */
  addingCount: number
  /** How many variants are about to be removed from the adjustment. */
  removingCount?: number
}

const variantCount = (count: number) => `${formatNumber(count, 0)} variant${count !== 1 ? "s" : ""}`

export const AdjustmentResplitWarning = ({ adjustment, addingCount, removingCount = 0 }: Props) => {
  const existingCount = adjustment.variantIds.length
  const finalCount = existingCount + addingCount - removingCount

  if (adjustment.type === "percentage" || existingCount === 0) return null
  if (addingCount <= 0 && removingCount <= 0) return null
  // Removing everything deletes the adjustment outright — nothing is left to re-split.
  if (finalCount <= 0) return null

  const total = formatNumber(adjustment.value)

  const lead =
    adjustment.type === "set"
      ? `This adjustment sets a total weekly demand of ${total}`
      : `This adjustment adds a total weekly uplift of ${total}`

  const isPureRemoval = removingCount > 0 && addingCount === 0
  const change =
    addingCount > 0 && removingCount > 0
      ? `Adding ${variantCount(addingCount)} and removing ${variantCount(removingCount)}`
      : addingCount > 0
        ? `Adding ${variantCount(addingCount)}`
        : `Removing ${variantCount(removingCount)}`

  return (
    <div
      role="status"
      className="flex gap-2 rounded-lg border bg-badge-orange-bg border-badge-orange-border px-3 py-2.5 text-badge-orange-fg-text"
    >
      <WarningIcon size={16} className="mt-0.5 shrink-0" />
      <div className="flex flex-col gap-0.5">
        <span className="label-sm">
          {isPureRemoval
            ? "Remaining variants will absorb the total"
            : "Existing variants will be re-split"}
        </span>
        <span className="paragraph-xs">
          {isPureRemoval ? (
            <>
              {lead}, shared across the variants on it. {change} still keeps the total at {total},
              so the {variantCount(finalCount)} left will absorb the removed quantities.
            </>
          ) : (
            <>
              {lead}, shared across the variants on it. {change} keeps the total at {total}, but it
              will be split across {variantCount(finalCount)}.
            </>
          )}
        </span>
      </div>
    </div>
  )
}
